import React, { useEffect, useState } from 'react';
import { Box, Button, Drawer, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';

/**
 * Per-widget settings sheet (Beta-1 plan P3-18): renders a widget's `settingsSchema` and hands
 * the values back as the layout item's `props`. Field types (SETTING_FIELD_TYPES):
 *   select  { options: [{ value, label }] }
 *   toggle  boolean
 *   period  30 d / 90 d / 12 m / all
 *   scope   an option list supplied by the page through `scopeOptions` ('' = follow the page);
 *           the page only offers scopes inside the viewer's allowed slice
 *   text    free text
 * Values are validated against the schema (unknown keys dropped, select values must be options).
 */
import { PERIOD_OPTIONS, sanitizeSettings } from './widgetSettings.js';

export { PERIOD_OPTIONS, sanitizeSettings };

const EMPTY = Object.freeze([]);

export function WidgetSettingsSheet({ open, widget, item, scopeOptions = EMPTY, onClose, onSave }) {
  const schema = widget?.settingsSchema || EMPTY;
  const [values, setValues] = useState({});
  // Re-seed the form only when the sheet opens for an item (not on every parent render — the
  // props objects above are recreated freely and would otherwise loop setState).
  useEffect(() => {
    if (open) setValues(sanitizeSettings(schema, item?.props, { scopeOptions }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.i, widget?.id]);
  const set = (key, value) => setValues((v) => ({ ...v, [key]: value }));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} sx={{ zIndex: (t) => t.zIndex.drawer + 2 }} PaperProps={{ sx: { width: { xs: '100%', sm: 380 }, p: 3 } }}>
      <Typography variant="h6" gutterBottom>{widget?.title || 'Widget'} settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These settings apply to this card only — add the widget twice to compare two views.
      </Typography>
      <Stack spacing={2}>
        {schema.map((field) => {
          const value = values[field.key];
          if (field.type === 'toggle') {
            return <FormControlLabel key={field.key} control={<Switch checked={Boolean(value)} onChange={(e) => set(field.key, e.target.checked)} />} label={field.label} />;
          }
          if (field.type === 'text') {
            return <TextField key={field.key} label={field.label} size="small" fullWidth value={value || ''} onChange={(e) => set(field.key, e.target.value)} helperText={field.help} />;
          }
          const options =
            field.type === 'period' ? PERIOD_OPTIONS
              : field.type === 'scope' ? [{ value: '', label: 'Follow the page filter' }, ...scopeOptions]
                : field.options.map((o) => (typeof o === 'object' ? o : { value: o, label: String(o) }));
          if (field.type === 'scope' && scopeOptions.length === 0) return null;
          return (
            <TextField key={field.key} select label={field.label} size="small" fullWidth value={value ?? ''} onChange={(e) => set(field.key, e.target.value)} helperText={field.help}>
              {options.map((o) => (
                <MenuItem key={String(o.value)} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          );
        })}
        {schema.length === 0 && <Typography variant="body2" color="text.secondary">This widget has no settings.</Typography>}
      </Stack>
      <Box sx={{ flex: 1 }} />
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(sanitizeSettings(schema, values, { scopeOptions }))} disabled={schema.length === 0}>Save</Button>
      </Stack>
    </Drawer>
  );
}

export default WidgetSettingsSheet;
